import { useQuery, useMutation, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";

export interface Topic {
    id: number;
    name: string;
    description: string;
    color: string;
    active: boolean;
    postCount: number;
    followerCount: number;
}

export interface CreateTopicData {
    name: string;
    description: string;
    color?: string;
    active?: boolean;
}

export interface TopicFilters {
    name?: string;
    active?: boolean;
}

const TOPICS_KEY = "topics";
const TRENDING_TOPICS_KEY = "trendingTopics";

export const useGetTopics = (filters?: TopicFilters) => {
    return useQuery({
        queryKey: [TOPICS_KEY, filters],
        queryFn: async () => {
            const response = await loggedInApiCall.get("/api/topics", {
                params: filters,
            });

            // Transform API Platform Hydra collection to expected format
            const hydraData = response.data;
            const topics = hydraData["hydra:member"] || [];

            return topics as Topic[];
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

export const useGetTopic = (topicId: number) => {
    return useQuery({
        queryKey: [TOPICS_KEY, topicId],
        queryFn: async () => {
            const response = await loggedInApiCall.get(`/api/topics/${topicId}`);
            return response.data as Topic;
        },
        enabled: !!topicId,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

export const useGetTrendingTopics = (type: "trending" | "popular" = "trending", limit: number = 10) => {
    return useQuery({
        queryKey: [TRENDING_TOPICS_KEY, type, limit],
        queryFn: async () => {
            const response = await loggedInApiCall.get("/api/social/topics/trending", {
                params: { type, limit },
            });
            return response.data.topics as Topic[];
        },
        staleTime: 1000 * 60 * 2, // 2 minutes for trending data
    });
};

export const useCreateTopic = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (topicData: CreateTopicData) => {
            console.info("Creating topic with data:", topicData);
            const response = await loggedInApiCall.post("/api/topics", topicData);
            console.info("Topic created, response:", response.data);
            return response.data as Topic;
        },
        onSuccess: () => {
            queryClient.invalidateQueries([TOPICS_KEY]);
            queryClient.invalidateQueries([TRENDING_TOPICS_KEY]);
        },
    });
};

export const useUpdateTopic = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, data }: { id: number; data: Partial<CreateTopicData> }) => {
            console.info(`Updating topic ${id} with data:`, data);
            const response = await loggedInApiCall.put(`/api/topics/${id}`, data);
            console.info("Topic updated, response:", response.data);
            return response.data as Topic;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries([TOPICS_KEY]);
            queryClient.invalidateQueries([TOPICS_KEY, data.id]);
            queryClient.invalidateQueries([TRENDING_TOPICS_KEY]);
        },
    });
};

export const useDeleteTopic = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (topicId: number) => {
            await loggedInApiCall.delete(`/api/topics/${topicId}`);
            return topicId;
        },
        onSuccess: () => {
            queryClient.invalidateQueries([TOPICS_KEY]);
            queryClient.invalidateQueries([TRENDING_TOPICS_KEY]);
        },
    });
};

// Search topics by name
export const useSearchTopics = (searchTerm: string) => {
    return useQuery({
        queryKey: [TOPICS_KEY, "search", searchTerm],
        queryFn: async () => {
            if (!searchTerm.trim()) return [];

            const response = await loggedInApiCall.get("/api/topics", {
                params: { name: searchTerm },
            });

            // Transform API Platform Hydra collection to expected format
            const hydraData = response.data;
            const topics = hydraData["hydra:member"] || [];

            return topics as Topic[];
        },
        enabled: !!searchTerm.trim(),
        staleTime: 1000 * 30, // 30 seconds for search results
    });
};
